/**
 * Real-voice test page for voice commands.
 * TTS speaks each phrase; live STT returns transcript; same detection logic marks pass/fail.
 * Route: /voice-test
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, Navigate } from "react-router-dom";
import io from "socket.io-client";
import { socketConfig } from "../utils/authConfig";
import { useGlobalStore } from "../context/UserContext/userContext";
import { isVoiceTestAllowedForUser } from "../utils/allowlists";
import { buildPatientAuthPath } from "../utils/buildPatientAuthUrl";
import { AUTH_SOURCE } from "../constants/authSource";
import {
  detectVoiceCommand,
  detectVoiceMacroCommand,
  detectVoiceTemplateCommand,
  detectTriggerlessTemplateCommand,
} from "../utils/voiceCommands";
import {
  EDITOR_COMMAND_TESTS,
  ENGLISH_PRODUCTION_TEMPLATE_VOICE_TESTS,
  HEBREW_PRODUCTION_TEMPLATE_VOICE_TESTS,
  MACRO_TESTS,
  MOCK_ENGLISH_PRODUCTION_TEMPLATES,
  MOCK_HEBREW_PRODUCTION_TEMPLATES,
  MOCK_MACROS,
  MOCK_PRODUCTION_TEMPLATES,
  MOCK_TEMPLATES,
  TEMPLATE_TESTS,
  TRIGGERLESS_PRODUCTION_TESTS,
  TRIGGERLESS_TEMPLATE_TESTS,
} from "./testData";
import BackButton from "../components/common/BackButton";
import DarkModeToggle from "../components/darkmodetoogle/DarkModeToggle";
import { VOICE_TEST_LANGUAGE_FILTER_OPTIONS } from "../constants/languages";

const TEST_SUITES = [
  { id: "editor", label: "Editor commands", tests: EDITOR_COMMAND_TESTS },
  { id: "template", label: "Template selection", tests: TEMPLATE_TESTS },
  {
    id: "hebrewTemplates",
    label: "Hebrew templates (real names)",
    tests: HEBREW_PRODUCTION_TEMPLATE_VOICE_TESTS,
  },
  {
    id: "englishTemplates",
    label: "English templates (real names)",
    tests: ENGLISH_PRODUCTION_TEMPLATE_VOICE_TESTS,
  },
  {
    id: "triggerlessTemplates",
    label: "Trigger-less template auto-match (edge cases)",
    tests: TRIGGERLESS_TEMPLATE_TESTS,
  },
  {
    id: "triggerlessReal",
    label: "Trigger-less templates (real names)",
    tests: TRIGGERLESS_PRODUCTION_TESTS,
  },
  { id: "macro", label: "Macro selection", tests: MACRO_TESTS },
];

const STT_DEBOUNCE_MS = 800;
const STT_TIMEOUT_MS = 12000;
const DELAY_BETWEEN_TESTS_MS = 1500;

function speakWithTTS(phrase, lang, voice = null) {
  return new Promise((resolve) => {
    if (!window.speechSynthesis) {
      resolve();
      return;
    }
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(phrase);
    u.lang = lang || "en-US";
    u.rate = 0.9;
    if (voice) u.voice = voice;
    u.onend = () => resolve();
    u.onerror = () => resolve();
    window.speechSynthesis.speak(u);
  });
}

function cancelTTS() {
  if (typeof window !== "undefined" && window.speechSynthesis) {
    window.speechSynthesis.cancel();
  }
}

/** Short label for "which command was under test" (for fail messages). */
function getExpectedCommandLabel(test, suiteId) {
  if (suiteId === "editor" && test.expected) {
    const a = test.expected.type && test.expected.action ? `${test.expected.type}/${test.expected.action}` : "";
    return test.expected.headerName != null ? `${a} → "${test.expected.headerName}"` : a;
  }
  if (
    (suiteId === "template" ||
      suiteId === "hebrewTemplates" ||
      suiteId === "englishTemplates" ||
      suiteId === "triggerlessTemplates" ||
      suiteId === "triggerlessReal") &&
    test.expectedTemplateName
  ) {
    return `template: ${test.expectedTemplateName}`;
  }
  if (suiteId === "macro" && test.expectedMacroName) return `macro: ${test.expectedMacroName}`;
  return "";
}

function evaluateEditorResult(transcript, lang, expected) {
  const cmd = detectVoiceCommand(transcript, lang);
  const expectedLabel = expected ? `${expected.type}/${expected.action}` : "";
  if (!cmd) {
    return { pass: false, detail: `Not recognized as command (treated as dictation). Expected: ${expectedLabel}` };
  }
  if (!expected || cmd.type !== expected.type || cmd.action !== expected.action) {
    return { pass: false, detail: `Expected ${expected?.type}/${expected?.action}, got ${cmd.type}/${cmd.action}` };
  }
  if (expected.headerName != null) {
    const expectedNorm = (expected.headerName || "").toLowerCase();
    const gotNorm = (cmd.headerName || "").toLowerCase();
    if (expectedNorm !== gotNorm) {
      return { pass: false, detail: `Expected header "${expected.headerName}", got "${cmd.headerName}"` };
    }
  }
  return { pass: true, detail: `Command: ${cmd.type}/${cmd.action}` };
}

function evaluateTriggerlessTemplateResult(
  transcript,
  test,
  templates = MOCK_TEMPLATES,
) {
  const res = detectTriggerlessTemplateCommand(transcript, templates);
  const residual = res?.residual ?? "";
  const expectedLabel = test.expectedTemplateName
    ? ` (expected: template "${test.expectedTemplateName}")`
    : "";
  if (test.expectMatch && !test.expectNoTemplate) {
    const pass = !!res && res.template.templateName === test.expectedTemplateName;
    const detail = pass
      ? `Trigger-less template: ${res.template.templateName} (score ${res.score.toFixed(2)})`
      : res
        ? `Expected "${test.expectedTemplateName}", got "${res.template.templateName}" (score ${res.score.toFixed(2)})`
        : `No trigger-less match${expectedLabel}`;
    return { pass, detail, residual };
  }
  const pass = !res;
  const detail = pass
    ? "Correctly no trigger-less match"
    : `Unexpected match: ${res.template.templateName} (score ${res.score.toFixed(2)})${expectedLabel}`;
  return { pass, detail, residual };
}

function evaluateTemplateResult(transcript, test, templates = MOCK_TEMPLATES) {
  const res = detectVoiceTemplateCommand(transcript, templates);
  const residual = res?.residual ?? "";
  const expectedLabel = test.expectedTemplateName ? ` (expected: template "${test.expectedTemplateName}")` : "";
  if (test.expectMatch && !test.expectNoTemplate) {
    const pass = res?.matched && res?.template && res.template.templateName === test.expectedTemplateName;
    const detail = pass
      ? `Template: ${res.template.templateName}`
      : res?.noTemplate
        ? `Template not found${expectedLabel}`
        : res?.template
          ? `Expected "${test.expectedTemplateName}", got "${res.template.templateName}"`
          : `No template match${expectedLabel}`;
    return { pass, detail, residual };
  }
  const pass = res?.matched && res?.noTemplate;
  const detail = pass ? "Correctly no template" : (res?.template ? `Unexpected match: ${res.template.templateName}` : "Unexpected result") + expectedLabel;
  return { pass, detail, residual };
}

function evaluateMacroResult(transcript, test) {
  const res = detectVoiceMacroCommand(transcript, MOCK_MACROS);
  const expectedLabel = test.expectedMacroName ? ` (expected: macro "${test.expectedMacroName}")` : "";
  if (test.expectMatch && !test.expectNoMacro) {
    const pass = res?.matched && res?.macro && res.macro.macroName === test.expectedMacroName;
    const detail = pass
      ? `Macro: ${res.macro.macroName}`
      : res?.noMacro
        ? `Macro not found${expectedLabel}`
        : res?.macro
          ? `Expected "${test.expectedMacroName}", got "${res.macro.macroName}"`
          : `No macro match${expectedLabel}`;
    return { pass, detail };
  }
  const pass = res?.matched && res?.noMacro;
  const detail = pass ? "Correctly no macro" : (res?.macro ? `Unexpected match: ${res.macro.macroName}` : "Unexpected result") + expectedLabel;
  return { pass, detail };
}

function VoiceCommandTestPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { authSource, creUser, radicalUser, getAccessToken } =
    useGlobalStore();
  const isCreAuth = authSource === AUTH_SOURCE.CRE;
  const [suiteId, setSuiteId] = useState("editor");
  const [languageFilter, setLanguageFilter] = useState("");
  const [ttsVoices, setTtsVoices] = useState([]);
  const [ttsVoiceIndex, setTtsVoiceIndex] = useState(-1); // -1 = default (browser default voice)
  const [isRunning, setIsRunning] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState("idle");
  const [testIndex, setTestIndex] = useState(0);
  const [results, setResults] = useState([]);
  const [logLines, setLogLines] = useState([]);
  const [streamingTranscript, setStreamingTranscript] = useState("");
  const [error, setError] = useState(null);
  const [copyFeedback, setCopyFeedback] = useState(null);

  const socketRef = useRef(null);
  const streamRef = useRef(null);
  const processorRef = useRef(null);
  const sourceRef = useRef(null);
  const audioContextRef = useRef(null);
  const onFinalTranscriptRef = useRef(null);
  const timeoutRef = useRef(null);
  const processedForCurrentTestRef = useRef(false);
  const accumulatedFinalRef = useRef("");
  const debounceTimerRef = useRef(null);
  const reportSectionRef = useRef(null);
  const runTestsRef = useRef([]);
  const runSuiteIdRef = useRef("");
  const runNextTestTimeoutRef = useRef(null);
  const stopRequestedRef = useRef(false);

  const suite = TEST_SUITES.find((s) => s.id === suiteId) || TEST_SUITES[0];
  // Residual is only meaningful for template / trigger-less template suites
  // — it's the "remaining dictation after the matched template name is
  // peeled off". For editor / macro suites it would always be empty noise.
  const showResidualColumn =
    suiteId === "template" ||
    suiteId === "hebrewTemplates" ||
    suiteId === "englishTemplates" ||
    suiteId === "triggerlessTemplates" ||
    suiteId === "triggerlessReal";
  const tests = languageFilter
    ? suite.tests.filter((t) => t.lang === languageFilter)
    : suite.tests;
  const currentTest = tests[testIndex];
  const hasResults = results.length > 0;
  const passedCount = results.filter((r) => r.pass).length;
  const failedCount = results.filter((r) => !r.pass).length;

  useEffect(() => {
    const loadVoices = () => setTtsVoices(window.speechSynthesis?.getVoices() ?? []);
    loadVoices();
    if (window.speechSynthesis) {
      window.speechSynthesis.onvoiceschanged = loadVoices;
      return () => { window.speechSynthesis.onvoiceschanged = null; };
    }
  }, []);

  const addLog = useCallback((line, type = "info") => {
    setLogLines((prev) => [...prev, { text: line, type }]);
  }, []);

  const stopAudio = useCallback(() => {
    cancelTTS();
    if (processorRef.current) {
      try { processorRef.current.disconnect(); } catch (_) {}
      processorRef.current = null;
    }
    if (sourceRef.current) {
      try { sourceRef.current.disconnect(); } catch (_) {}
      sourceRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (audioContextRef.current) {
      try { audioContextRef.current.close(); } catch (_) {}
      audioContextRef.current = null;
    }
    const socket = socketRef.current;
    if (socket) {
      if (socket.connected) socket.emit("endAudio");
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
    }
    setStreamingTranscript("");
  }, []);

  const connectSocket = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (socketRef.current?.connected) {
        setConnectionStatus("ready");
        resolve(socketRef.current);
        return;
      }
      if (socketRef.current) {
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      setConnectionStatus("connecting");
      const socket = io(socketConfig.url, { ...socketConfig.options, autoConnect: false });
      socketRef.current = socket;
      const onConnect = () => {
        setConnectionStatus("ready");
        addLog("Connected to STT server", "success");
        resolve(socket);
      };
      const onConnectError = (err) => {
        setConnectionStatus("error");
        addLog("Connection failed: " + (err?.message || "unknown"), "error");
        reject(err);
      };
      const onTranscription = ({ transcript, isFinal }) => {
        if (isFinal && transcript?.trim()) {
          const t = transcript.trim();
          if (onFinalTranscriptRef.current) {
            accumulatedFinalRef.current = accumulatedFinalRef.current ? `${accumulatedFinalRef.current} ${t}` : t;
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = setTimeout(() => {
              debounceTimerRef.current = null;
              const full = accumulatedFinalRef.current.trim();
              accumulatedFinalRef.current = "";
              if (full && onFinalTranscriptRef.current) onFinalTranscriptRef.current(full);
            }, STT_DEBOUNCE_MS);
          }
        } else if (!isFinal) {
          setStreamingTranscript(transcript || "");
        }
      };
      socket.once("connect", onConnect);
      socket.once("connect_error", onConnectError);
      socket.on("transcription", onTranscription);
      socket.connect();
    });
  }, [addLog]);

  const startAudio = useCallback(async () => {
    const socket = socketRef.current;
    if (!socket?.connected) throw new Error("Socket not connected");
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    streamRef.current = stream;
    const sampleRate = 16000;
    const ctx = new AudioContext({ sampleRate });
    audioContextRef.current = ctx;
    const source = ctx.createMediaStreamSource(stream);
    const processor = ctx.createScriptProcessor(4096, 1, 1);
    processor.onaudioprocess = (e) => {
      const input = e.inputBuffer.getChannelData(0);
      const buffer = new ArrayBuffer(input.length * 2);
      const view = new DataView(buffer);
      for (let i = 0; i < input.length; i++) {
        const s = Math.max(-1, Math.min(1, input[i]));
        view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
      }
      if (socketRef.current?.connected) socketRef.current.emit("audioData", buffer);
    };
    source.connect(processor);
    processor.connect(ctx.destination);
    processorRef.current = processor;
    sourceRef.current = source;
    socket.emit("startAudio");
    addLog("Microphone streaming", "success");
  }, [addLog]);

  const runNextTest = useCallback(
    (nextIndex) => {
      if (stopRequestedRef.current) return;
      const runTests = runTestsRef.current;
      const currentSuiteId = runSuiteIdRef.current;
      if (nextIndex >= runTests.length) {
        setIsRunning(false);
        setConnectionStatus("idle");
        stopAudio();
        addLog("All tests finished.", "success");
        return;
      }
      const test = runTests[nextIndex];
      processedForCurrentTestRef.current = false;
      accumulatedFinalRef.current = "";
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      const socket = socketRef.current;
      if (socket?.connected) {
        socket.emit("setLanguage", { language: test.lang });
        socket.emit("setType", { type: null });
      }
      const ttsVoice = ttsVoiceIndex >= 0 && ttsVoices[ttsVoiceIndex] ? ttsVoices[ttsVoiceIndex] : null;
      speakWithTTS(test.phraseToSpeak, test.lang, ttsVoice);
      const timeout = setTimeout(() => {
        if (processedForCurrentTestRef.current) return;
        processedForCurrentTestRef.current = true;
        const expectedLabel = getExpectedCommandLabel(test, currentSuiteId);
        const timeoutDetail = expectedLabel ? `Timeout waiting for STT (expected: ${expectedLabel})` : "Timeout waiting for STT";
        setResults((r) => [...r, { test, transcript: "(no transcript)", pass: false, detail: timeoutDetail, residual: "" }]);
        setTestIndex(nextIndex + 1);
        if (runNextTestTimeoutRef.current) clearTimeout(runNextTestTimeoutRef.current);
        runNextTestTimeoutRef.current = setTimeout(() => runNextTest(nextIndex + 1), DELAY_BETWEEN_TESTS_MS);
      }, STT_TIMEOUT_MS);
      timeoutRef.current = timeout;
      onFinalTranscriptRef.current = (transcript) => {
        if (processedForCurrentTestRef.current) return;
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        processedForCurrentTestRef.current = true;
        let pass = false;
        let detail = "";
        let residual = "";
        if (currentSuiteId === "editor") {
          const out = evaluateEditorResult(transcript, test.lang, test.expected);
          pass = out.pass;
          detail = out.detail;
        } else if (currentSuiteId === "template") {
          const out = evaluateTemplateResult(transcript, test);
          pass = out.pass;
          detail = out.detail;
          residual = out.residual;
        } else if (currentSuiteId === "hebrewTemplates") {
          const out = evaluateTemplateResult(transcript, test, MOCK_HEBREW_PRODUCTION_TEMPLATES);
          pass = out.pass;
          detail = out.detail;
          residual = out.residual;
        } else if (currentSuiteId === "englishTemplates") {
          const out = evaluateTemplateResult(transcript, test, MOCK_ENGLISH_PRODUCTION_TEMPLATES);
          pass = out.pass;
          detail = out.detail;
          residual = out.residual;
        } else if (currentSuiteId === "triggerlessTemplates") {
          const out = evaluateTriggerlessTemplateResult(transcript, test);
          pass = out.pass;
          detail = out.detail;
          residual = out.residual;
        } else if (currentSuiteId === "triggerlessReal") {
          // Combined Hebrew + English template list — exercises the engine
          // against a realistic multilingual user library. The language
          // dropdown filters the rows shown / spoken at runtime.
          const out = evaluateTriggerlessTemplateResult(
            transcript,
            test,
            MOCK_PRODUCTION_TEMPLATES,
          );
          pass = out.pass;
          detail = out.detail;
          residual = out.residual;
        } else {
          const out = evaluateMacroResult(transcript, test);
          pass = out.pass;
          detail = out.detail;
        }
        setResults((r) => [...r, { test, transcript, pass, detail, residual }]);
        setTestIndex(nextIndex + 1);
        onFinalTranscriptRef.current = null;
        if (runNextTestTimeoutRef.current) clearTimeout(runNextTestTimeoutRef.current);
        runNextTestTimeoutRef.current = setTimeout(() => runNextTest(nextIndex + 1), DELAY_BETWEEN_TESTS_MS);
      };
    },
    [addLog, stopAudio, ttsVoiceIndex, ttsVoices],
  );

  const startTest = useCallback(async () => {
    const runTests = languageFilter ? suite.tests.filter((t) => t.lang === languageFilter) : suite.tests;
    if (runTests.length === 0) {
      setError("No tests for the selected suite and language.");
      return;
    }
    setError(null);
    setResults([]);
    setLogLines([]);
    setTestIndex(0);
    runTestsRef.current = runTests;
    runSuiteIdRef.current = suiteId;
    setIsRunning(true);
    setConnectionStatus("connecting");
    addLog(`Starting: ${suite.label} (${runTests.length} tests)`, "info");
    stopRequestedRef.current = false;
    try {
      await connectSocket();
      // Short delay so socket/stream is ready and nothing is missed when TTS starts
      await new Promise((r) => setTimeout(r, 800));
      await startAudio();
      setConnectionStatus("ready");
      runNextTest(0);
    } catch (e) {
      setError(e?.message || "Failed to start");
      setConnectionStatus("error");
      setIsRunning(false);
      addLog("Error: " + (e?.message || e), "error");
    }
  }, [suiteId, suite.label, suite.tests, languageFilter, connectSocket, startAudio, runNextTest, addLog]);

  const stopTest = useCallback(() => {
    stopRequestedRef.current = true;
    if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
    if (runNextTestTimeoutRef.current) { clearTimeout(runNextTestTimeoutRef.current); runNextTestTimeoutRef.current = null; }
    if (debounceTimerRef.current) { clearTimeout(debounceTimerRef.current); debounceTimerRef.current = null; }
    onFinalTranscriptRef.current = null;
    cancelTTS();
    stopAudio();
    setConnectionStatus("idle");
    setIsRunning(false);
    addLog("Stopped by user", "info");
  }, [stopAudio, addLog]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (runNextTestTimeoutRef.current) clearTimeout(runNextTestTimeoutRef.current);
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      cancelTTS();
      stopAudio();
    };
  }, [stopAudio]);

  useEffect(() => {
    if (hasResults && !isRunning && reportSectionRef.current) {
      reportSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [hasResults, isRunning]);

  const reportText = [
    "Voice commands – real-voice test report",
    "Suite: " + suite.label,
    "Date: " + new Date().toISOString(),
    "",
    ...results.map((r) => {
      const status = r.pass ? "PASS" : "FAIL";
      const residualSegment = r.residual ? ` | residual: "${r.residual}"` : "";
      return `[${status}] ${r.test.lang} "${r.test.phraseToSpeak}" | heard: "${r.transcript}"${residualSegment} | ${r.detail}`;
    }),
    "",
    `Summary: ${passedCount} passed, ${failedCount} failed`,
  ].join("\n");

  const copyReport = useCallback(
    (e) => {
      e?.preventDefault?.();
      if (!navigator.clipboard?.writeText) {
        setCopyFeedback("Clipboard not available");
        setTimeout(() => setCopyFeedback(null), 2000);
        return;
      }
      navigator.clipboard.writeText(reportText).then(() => {
        setCopyFeedback("Copied");
        addLog("Report copied to clipboard", "success");
        setTimeout(() => setCopyFeedback(null), 2000);
      }).catch(() => {
        setCopyFeedback("Copy failed");
        setTimeout(() => setCopyFeedback(null), 2000);
      });
    },
    [reportText, addLog],
  );

  const patientBackPath = (() => {
    const token =
      getAccessToken?.() ??
      new URLSearchParams(location.search).get("token") ??
      "";
    const user = isCreAuth ? creUser : radicalUser;
    const systemCode = isCreAuth ? AUTH_SOURCE.CRE : AUTH_SOURCE.RADICAL;
    return buildPatientAuthPath({
      token,
      user,
      systemCode,
      existingSearch: location.search,
    });
  })();

  const downloadReport = useCallback(
    (e) => {
      e?.preventDefault?.();
      const blob = new Blob([reportText], { type: "text/plain" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `voice-commands-report-${suiteId}-${Date.now()}.txt`;
      a.click();
      URL.revokeObjectURL(a.href);
      addLog("Report downloaded", "success");
    },
    [reportText, suiteId, addLog],
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      <div className="max-w-4xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-6 flex items-center justify-between gap-4">
          <div className="max-w-xs">
            <BackButton
              text="Back to report"
              onClick={() => navigate(patientBackPath)}
            />
          </div>
          <DarkModeToggle />
        </div>
        <header className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
            Voice commands – real-voice test
          </h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 max-w-2xl">
            Uses browser TTS to speak each command and your app’s live STT to transcribe. Allow the microphone
            and use speakers so the mic picks up the TTS (or say the phrase yourself when prompted).
          </p>
        </header>

        <section className="mb-6 p-4 sm:p-5 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
          <h2 className="sr-only">Test configuration</h2>
          <div className="flex flex-col gap-4">
            <div className="flex flex-nowrap items-center gap-3 overflow-x-auto">
              <div className="flex items-center gap-2">
                <label htmlFor="suite-select" className="text-sm font-medium text-gray-700 dark:text-gray-300">Suite</label>
                <select
                  id="suite-select"
                  value={suiteId}
                  onChange={(e) => setSuiteId(e.target.value)}
                  disabled={isRunning}
                  aria-label="Test suite"
                  className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 w-[190px] min-w-[190px]"
                >
                  {TEST_SUITES.map((s) => (
                    <option key={s.id} value={s.id}>{s.label} ({s.tests.length})</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <label htmlFor="lang-select" className="text-sm font-medium text-gray-700 dark:text-gray-300">Language</label>
                <select
                  id="lang-select"
                  value={languageFilter}
                  onChange={(e) => setLanguageFilter(e.target.value)}
                  disabled={isRunning}
                  aria-label="Language filter"
                  className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 w-[190px] min-w-[190px]"
                >
                  {VOICE_TEST_LANGUAGE_FILTER_OPTIONS.map((opt) => (
                    <option key={opt.value || "all"} value={opt.value}>
                      {opt.label}
                      {opt.value ? ` (${suite.tests.filter((t) => t.lang === opt.value).length})` : ` (${suite.tests.length})`}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <label htmlFor="tts-voice-select" className="text-sm font-medium text-gray-700 dark:text-gray-300">TTS voice</label>
                <select
                  id="tts-voice-select"
                  value={ttsVoiceIndex}
                  onChange={(e) => setTtsVoiceIndex(Number(e.target.value))}
                  disabled={isRunning}
                  aria-label="TTS voice (male/female)"
                  className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 w-[170px] min-w-[170px]"
                >
                  <option value={-1}>Default</option>
                  {ttsVoices.map((v, i) => (
                    <option key={`${v.name}-${v.lang}-${i}`} value={i}>
                      {v.name} ({v.lang})
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {connectionStatus === "connecting" && (
                <span className="text-sm text-amber-600 dark:text-amber-400" aria-live="polite">Connecting…</span>
              )}
              {!isRunning ? (
                <button type="button" onClick={startTest} disabled={tests.length === 0} aria-label="Start voice test"
                  className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg font-medium text-sm shadow-sm transition-colors">
                  Start test
                </button>
              ) : (
                <button type="button" onClick={stopTest} aria-label="Stop voice test"
                  className="px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium text-sm shadow-sm transition-colors">
                  Stop test
                </button>
              )}
            </div>
          </div>
          {tests.length === 0 && (
            <p className="mt-3 text-sm text-amber-600 dark:text-amber-400">No tests for this language. Choose another language or suite.</p>
          )}
        </section>

        {error && (
          <div className="mb-6 p-4 rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200" role="alert">
            {error}
          </div>
        )}

        {isRunning && currentTest && (
          <div className="mb-6 p-4 rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20" aria-live="polite">
            <div className="flex items-center gap-2 text-blue-800 dark:text-blue-200 font-medium">
              <span className="inline-flex h-2 w-2 rounded-full bg-blue-500 animate-pulse" aria-hidden />
              Test {testIndex + 1} of {runTestsRef.current.length}
            </div>
            <p className="mt-1 text-blue-900 dark:text-blue-100">{currentTest.lang} – &quot;{currentTest.phraseToSpeak}&quot;</p>
            {streamingTranscript && (
              <p className="mt-2 text-sm text-blue-700 dark:text-blue-300 truncate">Hearing: {streamingTranscript}</p>
            )}
          </div>
        )}

        {(hasResults && !isRunning) && (
          <div ref={reportSectionRef} className="mb-6 flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Report:</span>
            <button type="button" onClick={copyReport} className="px-3 py-2 rounded-lg bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 font-medium text-sm transition-colors">
              {copyFeedback === "Copied" ? "Copied!" : copyFeedback || "Copy report"}
            </button>
            <button type="button" onClick={downloadReport} className="px-3 py-2 rounded-lg bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 font-medium text-sm transition-colors">
              Download report
            </button>
          </div>
        )}

        {hasResults && (
          <div className="mb-6 p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Summary</h2>
            <div className="flex gap-4">
              <span className="text-emerald-600 dark:text-emerald-400 font-medium">{passedCount} passed</span>
              <span className="text-red-600 dark:text-red-400 font-medium">{failedCount} failed</span>
            </div>
          </div>
        )}

        <section className="mb-6">
          <h2 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Log</h2>
          <div className="p-3 rounded-xl bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 max-h-40 overflow-y-auto font-mono text-xs" role="log" aria-label="Test log">
            {logLines.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400">No log entries yet.</p>
            ) : (
              logLines.map((l, i) => (
                <div key={`${i}-${l.text.slice(0, 20)}`} className={l.type === "error" ? "text-red-600 dark:text-red-400" : l.type === "success" ? "text-emerald-600 dark:text-emerald-400" : "text-gray-700 dark:text-gray-300"}>
                  {l.text}
                </div>
              ))
            )}
          </div>
        </section>

        <section>
          <h2 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Results</h2>
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-800 shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm" aria-label="Test results">
                <thead className="bg-gray-100 dark:bg-gray-800">
                  <tr>
                    <th className="p-3 font-medium text-gray-700 dark:text-gray-300">Lang</th>
                    <th className="p-3 font-medium text-gray-700 dark:text-gray-300">Phrase</th>
                    <th className="p-3 font-medium text-gray-700 dark:text-gray-300">Heard</th>
                    {showResidualColumn && (
                      <th
                        className="p-3 font-medium text-gray-700 dark:text-gray-300"
                        title="What remains after the template name is peeled off — would be inserted as dictation"
                      >
                        Residual
                      </th>
                    )}
                    <th className="p-3 font-medium text-gray-700 dark:text-gray-300">Result</th>
                  </tr>
                </thead>
                <tbody>
                  {results.length === 0 ? (
                    <tr>
                      <td colSpan={showResidualColumn ? 5 : 4} className="p-6 text-center text-gray-500 dark:text-gray-400">No results yet. Start a test to see results here.</td>
                    </tr>
                  ) : (
                    results.map((r, i) => (
                      <tr key={`${r.test.id ?? i}-${r.transcript?.slice(0, 15)}`} className={r.pass ? "bg-emerald-50/50 dark:bg-emerald-900/10 border-b border-gray-100 dark:border-gray-700/50" : "bg-red-50/50 dark:bg-red-900/10 border-b border-gray-100 dark:border-gray-700/50"}>
                        <td className="p-3">{r.test.lang}</td>
                        <td className="p-3 font-medium">&quot;{r.test.phraseToSpeak}&quot;</td>
                        <td className="p-3 text-gray-600 dark:text-gray-400">&quot;{r.transcript}&quot;</td>
                        {showResidualColumn && (
                          <td className="p-3 text-gray-600 dark:text-gray-400">
                            {r.residual ? `"${r.residual}"` : <span className="text-gray-400 dark:text-gray-500">—</span>}
                          </td>
                        )}
                        <td className="p-3">
                          <span className={r.pass ? "text-emerald-600 dark:text-emerald-400 font-medium" : "text-red-600 dark:text-red-400 font-medium"}>{r.pass ? "PASS" : "FAIL"}</span>
                          <span className="text-gray-600 dark:text-gray-400"> – {r.detail}</span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

function VoiceTestPage() {
  const location = useLocation();
  const {
    authSource,
    creUser,
    radicalUser,
    getAccessToken,
  } = useGlobalStore();
  const isCreAuth = authSource === AUTH_SOURCE.CRE;
  const isRadicalAuth = authSource === AUTH_SOURCE.RADICAL;
  const allowed = isVoiceTestAllowedForUser({
    isCreAuth,
    isRadicalAuth,
    creUser,
    radicalUser,
  });
  if (!allowed) {
    const token =
      getAccessToken?.() ??
      new URLSearchParams(location.search).get("token") ??
      "";
    const user = isCreAuth ? creUser : radicalUser;
    const systemCode = isCreAuth ? AUTH_SOURCE.CRE : AUTH_SOURCE.RADICAL;
    return (
      <Navigate
        to={buildPatientAuthPath({
          token,
          user,
          systemCode,
          existingSearch: location.search,
        })}
        replace
      />
    );
  }
  return <VoiceCommandTestPage />;
}

export default VoiceTestPage;
